// src/pages/pengamatan/sensus-populasi-anakan-pisang/hooks/useSensusPopulasiAnakanPisangData.js
import { useState, useEffect } from "react";
import { database } from "../../../../firebase";
import { ref, onValue, off, update } from "firebase/database";

const useSensusPopulasiAnakanPisangData = (user, userProfile) => {
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [userJabatan, setUserJabatan] = useState(null);
    const [userRoleNames, setUserRoleNames] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fungsi untuk mengupdate status pengamatan per plot
    const updateStatusPengamatan = async (selectedItems, newStatus, onSuccess) => {
        try {
            const updates = {};
            const updatedItems = [];
            
            selectedItems.forEach(itemId => {
                const [sensusKey, plotKey] = itemId.split('-');
                console.log('Updating:', sensusKey, plotKey, 'to:', newStatus);
                
                const path = `db_freshpine/db_pengamatan/sensus_populasi_anakan_pisang/${sensusKey}/plot/${plotKey}/status_pengamatan_per_plot`;
                updates[path] = newStatus;
                updatedItems.push(itemId);
            });

            console.log('Batch updates:', updates);
            
            // Update UI langsung terlebih dahulu (optimistic update)
            setOrders(prevOrders => 
                prevOrders.map(order => 
                    updatedItems.includes(order.id) 
                        ? { ...order, status_pengamatan: newStatus }
                        : order
                )
            );
            
            await update(ref(database), updates);
            
            if (onSuccess) {
                onSuccess();
            }
            
            return true;
        } catch (error) {
            console.error("Error updating status:", error);
            setRefreshTrigger(prev => prev + 1);
            return false;
        }
    };

    // Fungsi untuk mengubah format tanggal DD-MM-YYYY menjadi YYYYMMDD untuk sorting
    const formatDateForSorting = (dateString) => {
        if (!dateString || dateString === '-') return '00000000';
        
        try {
            const [day, month, year] = dateString.split('-');
            if (day && month && year) {
                return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
            }
            return '00000000';
        } catch (error) {
            console.error('Error formatting date for sorting:', error);
            return '00000000';
        }
    };

    // Fungsi untuk menghitung nilai setelah perkalian
    const calculateMultipliedValue = (value, multiplier) => {
        const numValue = parseInt(value) || 0;
        return numValue * multiplier;
    };

    // Fungsi untuk menghitung total dari semua nilai yang sudah dikalikan
    const calculateTotal = (rta, rda1, rda2, rda3, rda4, rdat1, rdat2, rdat3, rdat4) => {
        return rta + rda1 + rda2 + rda3 + rda4 + rdat1 + rdat2 + rdat3 + rdat4;
    };

    useEffect(() => {
        if (!user || !userProfile) {
            setLoadingOrders(true);
            return;
        }

        console.log('User UID:', user.uid);
        console.log('User Profile:', userProfile);

        // Ambil data jabatan user dan role
        const userRef = ref(database, `db_user/tb_user/${user.uid}`);
        
        const unsubscribeUser = onValue(userRef, (userSnapshot) => {
            const userData = userSnapshot.val();
            console.log('User Data from Firebase:', userData);
            
            if (userData) {
                // Ambil data jabatan
                if (userData.id_jabatan) {
                    console.log('ID Jabatan:', userData.id_jabatan);
                    
                    const jabatanRef = ref(database, `db_config/db_wfms/tb_jabatan/${userData.id_jabatan}`);
                    
                    onValue(jabatanRef, (jabatanSnapshot) => {
                        const jabatanData = jabatanSnapshot.val();
                        console.log('Jabatan Data:', jabatanData);
                        
                        if (jabatanData) {
                            console.log('Nama Jabatan:', jabatanData.nama_jabatan);
                            setUserJabatan(jabatanData.nama_jabatan);
                        } else {
                            console.log('Jabatan data not found for ID:', userData.id_jabatan);
                        }
                    }, (error) => {
                        console.error('Error fetching jabatan data:', error);
                    });
                }

                // Ambil data role
                if (userData.id_role) {
                    console.log('ID Role:', userData.id_role);
                    
                    const roleIds = userData.id_role.split(';');
                    
                    const rolePromises = roleIds.map(roleId => {
                        const roleRef = ref(database, `db_config/db_wfms/tb_role/${roleId}`);
                        return new Promise((resolve) => {
                            onValue(roleRef, (roleSnapshot) => {
                                const roleData = roleSnapshot.val();
                                resolve(roleData ? roleData.nama_role : null);
                            }, { onlyOnce: true });
                        });
                    });
                    
                    Promise.all(rolePromises).then(roleNames => {
                        const filteredRoleNames = roleNames.filter(name => name !== null);
                        console.log('User Role Names:', filteredRoleNames);
                        setUserRoleNames(filteredRoleNames);
                    });
                }
            } else {
                console.log('User data not found');
            }
        }, (error) => {
            console.error('Error fetching user data:', error);
        });

        const sensusRef = ref(database, 'db_freshpine/db_pengamatan/sensus_populasi_anakan_pisang');
        
        const unsubscribeSensus = onValue(sensusRef, (snapshot) => {
            const sensusData = [];
            
            // Process sensus data sesuai struktur database yang baru
            snapshot.forEach((childSnapshot) => {
                const parentData = childSnapshot.val();
                const sensusKey = childSnapshot.key;
                
                // Iterasi melalui setiap plot
                if (parentData.plot) {
                    Object.keys(parentData.plot).forEach(plotKey => {
                        const plotData = parentData.plot[plotKey];
                        
                        // Hanya ambil data dengan status_pengamatan_per_plot = "Approved"
                        if (plotData.status_pengamatan_per_plot === "Approved" || plotData.status_pengamatan_per_plot === "Processed") {
                            // Format block: lokasi - fmp
                            const block = parentData.lokasi && parentData.fmp 
                                ? `${parentData.lokasi}-${parentData.fmp}`
                                : '-';
                            
                            // Ambil nilai asli dari database
                            const rawRta = plotData.data_entry?.rumpun_tanpa_anakan?.rta_1 || '0';
                            const rawRda1 = plotData.data_entry?.rumpun_dengan_anakan?.rda_1 || '0';
                            const rawRda2 = plotData.data_entry?.rumpun_dengan_anakan?.rda_2 || '0';
                            const rawRda3 = plotData.data_entry?.rumpun_dengan_anakan?.rda_3 || '0';
                            const rawRda4 = plotData.data_entry?.rumpun_dengan_anakan?.rda_4 || '0';
                            const rawRdat1 = plotData.data_entry?.rumpun_dengan_anakan_tagging?.rdat_1 || '0';
                            const rawRdat2 = plotData.data_entry?.rumpun_dengan_anakan_tagging?.rdat_2 || '0';
                            const rawRdat3 = plotData.data_entry?.rumpun_dengan_anakan_tagging?.rdat_3 || '0';
                            const rawRdat4 = plotData.data_entry?.rumpun_dengan_anakan_tagging?.rdat_4 || '0';
                            
                            // Hitung nilai setelah perkalian
                            const rta = calculateMultipliedValue(rawRta, 1);
                            const rda_1 = calculateMultipliedValue(rawRda1, 1);
                            const rda_2 = calculateMultipliedValue(rawRda2, 2);
                            const rda_3 = calculateMultipliedValue(rawRda3, 3);
                            const rda_4 = calculateMultipliedValue(rawRda4, 4);
                            const rdat_1 = calculateMultipliedValue(rawRdat1, 1);
                            const rdat_2 = calculateMultipliedValue(rawRdat2, 2);
                            const rdat_3 = calculateMultipliedValue(rawRdat3, 3);
                            const rdat_4 = calculateMultipliedValue(rawRdat4, 4);
                            
                            // Hitung total dari semua nilai yang sudah dikalikan
                            const total = calculateTotal(
                                rta, rda_1, rda_2, rda_3, rda_4, 
                                rdat_1, rdat_2, rdat_3, rdat_4
                            );
                            
                            // Gunakan sensusKey dan plotKey untuk membuat ID yang unik
                            sensusData.push({
                                id: `${sensusKey}-${plotKey}`,
                                sensusKey: sensusKey,
                                plotKey: plotKey,
                                status_pengamatan: plotData.status_pengamatan_per_plot || '-',
                                tanggal_pengamatan: parentData.tanggal_pengamatan || '-',
                                tanggal_pengamatan_sort: formatDateForSorting(parentData.tanggal_pengamatan),
                                block: block,
                                plot: plotData.plot || '-',
                                luas: plotData.luas_plot || '-',
                                
                                // Data untuk Rumpun Tanpa Anakan (sudah dikalikan 1)
                                rta: rta.toString(),
                                
                                // Data untuk Rumpun Dengan Anakan (sudah dikalikan)
                                rda_1: rda_1.toString(),
                                rda_2: rda_2.toString(),
                                rda_3: rda_3.toString(),
                                rda_4: rda_4.toString(),
                                
                                // Data untuk Rumpun Dengan Anakan Tanpa Tagging (sudah dikalikan)
                                rdat_1: rdat_1.toString(),
                                rdat_2: rdat_2.toString(),
                                rdat_3: rdat_3.toString(),
                                rdat_4: rdat_4.toString(),
                                
                                // Total dari semua nilai yang sudah dikalikan
                                total: total.toString(),
                                keterangan: '-' // Default value untuk keterangan
                            });
                        }
                    });
                }
            });

            // Sorting data berdasarkan: tanggal -> block -> plot (ASCENDING - terlama di atas)
            const sortedData = sensusData.sort((a, b) => {
                // 1. Urutkan berdasarkan tanggal (ascending - terlama di atas, terbaru di bawah)
                const dateCompare = a.tanggal_pengamatan_sort.localeCompare(b.tanggal_pengamatan_sort);
                if (dateCompare !== 0) return dateCompare;
                
                // 2. Jika tanggal sama, urutkan berdasarkan block (ascending)
                const blockCompare = a.block.localeCompare(b.block);
                if (blockCompare !== 0) return blockCompare;
                
                // 3. Jika block sama, urutkan berdasarkan plot (numerik ascending)
                const plotA = parseInt(a.plot) || 0;
                const plotB = parseInt(b.plot) || 0;
                
                return plotA - plotB;
            });
            
            setOrders(sortedData);
            setLoadingOrders(false);
            
            console.log('Sensus populasi anakan pisang data loaded and sorted:', sortedData);
        }, (error) => {
            console.error("Error fetching sensus populasi anakan pisang data:", error);
            setLoadingOrders(false);
            setSnackbar({
                open: true,
                message: "Gagal memuat data sensus populasi anakan pisang",
                severity: "error"
            });
        });
        
        return () => {
            off(userRef);
            off(sensusRef, 'value');
        };
    }, [user, userProfile, refreshTrigger]);

    return { 
        orders,
        loadingOrders,
        snackbar,
        setSnackbar,
        userJabatan,
        userRoleNames,
        updateStatusPengamatan 
    };
};

export default useSensusPopulasiAnakanPisangData;